import moment from "moment";
import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {
    getColorByHashedKey,
    resolveColorHexByNameWithWCAG,
} from "../../../utils/ColorUtils";
import {utcDateFormatter, utcMomentToLocal} from "../../../utils/DateTimeUtils";
import {isBetween, isFiniteNumber} from "../../../utils/MathUtils";
import {getAnimalUnit} from "../../../utils/SettingsUtils";
import {convertWeightUnitTo, getUnit} from "../../../utils/UnitUtils";
import Chart from "../../basics/chart/Chart";
import CompareWeightsTooltip from "../../charts/CompareWeightsTooltip";
import {calculateAutoDomainForFallChart} from "../ideal-charts/charts/utils";
import {filterMap} from "../../../utils/Utils";
import useDarkMode from "../../../hooks/useDarkMode";
import {getOtherWeightsSelector} from "../../../selectors/settingsSelector";
import {useSelector} from "react-redux";
import EstimatedWeight from "../../../beans/estimated-weight/EstimatedWeight";

const emptyArray = [];

function FallChart({
    fallDict,
    start,
    end,
    chartDomain = emptyArray,
    referenceLines,
    birthTime,
}) {
    const {t} = useTranslation();

    const animalUnit = useMemo(() => getAnimalUnit(), []);

    const weightUnit = useMemo(
        () => getUnit("weight", animalUnit),
        [animalUnit]
    );

    const weightFormatter = useCallback(
        (value) => {
            if (!isFiniteNumber(value)) return null;
            return convertWeightUnitTo(value, {
                showUnit: false,
                unit: animalUnit,
                rawValue: true,
                fixed: 2,
            });
        },
        [animalUnit]
    );

    const weightArray = useSelector(getOtherWeightsSelector);

    const dataSet = useMemo(() => {
        const ds = [];
        const [chartStart, chartEnd] = chartDomain;
        const diff = Math.abs(
            moment.utc(chartStart).diff(moment.utc(chartEnd), "day")
        );
        const estimate = new EstimatedWeight(0, []);
        estimate.setWeightTable(weightArray, 1200);
        estimate.setBirthDate(birthTime);
        for (let i = 0; i <= diff; i++) {
            const dayUTC = moment.utc(chartStart).add(i, "day");
            const dayLOCAL = utcMomentToLocal(dayUTC);
            const estimated = estimate.getWeightByDate(+dayLOCAL);
            const isGroupActive = isBetween(+dayUTC, start, end);
            if (!isGroupActive) {
                ds.push({
                    time: +dayUTC,
                    estimated,
                });
                continue;
            }

            // getting events by utc day from dict
            const eventsForCurrentDay = fallDict[+dayUTC] ?? null;
            if (Array.isArray(eventsForCurrentDay)) {
                for (const event of eventsForCurrentDay) {
                    ds.push({
                        ...event,
                        time: +dayUTC,
                        estimated,
                    });
                }
            } else {
                ds.push({
                    time: +dayUTC,
                    estimated,
                });
            }
        }
        return ds;
    }, [chartDomain, start, end, fallDict, birthTime, weightArray]);

    const chartDomainY = useMemo(() => {
        return calculateAutoDomainForFallChart(dataSet);
    }, [dataSet]);

    const dataDef = useMemo(() => {
        const usedReasons = {};
        const tmp = [
            {
                dataKey: "estimated",
                color: "purple",
                strokeDasharray: "5 5",
                strokeWidth: 4,
                type: "linear",
                chartType: "Line",
                unit: weightUnit,
                name: t("expectedWeight"),
                yAxisId: "lineAxis",
                formatter: weightFormatter,
            },
        ];
        for (let item of dataSet) {
            if (item.Reason) {
                const color = getColorByHashedKey(item.Reason);
                if (!usedReasons[item.Reason]) {
                    usedReasons[item.Reason] = item.Reason;
                    tmp.push({
                        dataKey: item.Reason,
                        color: color,
                        chartType: "Scatter",
                        opacity: 1,
                        unit: weightUnit,
                        name: item.Reason,
                        yAxisId: "lineAxis",
                        formatter: weightFormatter,
                    });
                }
            }
        }
        return tmp;
    }, [weightFormatter, weightUnit, dataSet, t]);

    const darkMode = useDarkMode();

    // This prepares a flattened dataset for the tooltip,
    // enabling it to display multiple values that share the same X coordinate (time).
    // For each data point and each defined dataKey present in that point,
    // it creates a separate tooltip entry enriched with display properties like color, formatter, and unit.
    const tooltipContent = useMemo(() => {
        const tooltipDataSet = dataSet.flatMap((item) => {
            return filterMap(dataDef, (def) =>
                item.hasOwnProperty(def.dataKey)
                    ? {
                          dataKey: def.dataKey,
                          value: item[def.dataKey],
                          name: def.name,
                          unit: def.unit,
                          fill: resolveColorHexByNameWithWCAG(def.color),
                          stroke: def.color,
                          formatter: def.formatter,
                          time: item.time,
                          payload: item,
                      }
                    : null
            );
        });
        return <CompareWeightsTooltip data={tooltipDataSet} />;
        // eslint-disable-next-line
    }, [dataDef, dataSet, darkMode]);

    return (
        <div className={"h-0 mh-15rem"}>
            <Chart
                data={dataSet}
                dataDef={dataDef}
                syncId={"idealChart"}
                isAnimationActive={false}
                tooltipContent={tooltipContent}
                syncMethod={"value"}
                saveAsExcell={t("printSelectedAnimalsModal.falls")}
                Yaxis={{
                    name: t("weight"),
                    yAxisId: "lineAxis",
                    type: "number",
                    domain: chartDomainY,
                    formatter: weightFormatter,
                }}
                Xaxis={{
                    name: "",
                    domain: chartDomain,
                    dataKey: "time",
                    formatter: utcDateFormatter,
                    type: "number",
                }}
                type={"Composed"}
                referenceLines={referenceLines}
            />
            {referenceLines && referenceLines.length === 0 && (
                <div className="chart-template-no-data">
                    <h5>{t("chartDisplayNoBirthTime")}</h5>
                    <div>{t("chartDisplayNoBirthTimeText")}</div>
                </div>
            )}
        </div>
    );
}

export default React.memo(FallChart);
